/**
 * Iter XX.242 · Cricket Graph card — returning-user snapshot.
 *
 * Shows the three founder-mandated returning-user questions:
 *   1. Where am I?           → goal + current focus + last activity
 *   2. What changed?         → most recent baseline / personal best
 *   3. What's next?          → deterministic next_step from the graph
 *
 * Sections without grounded data are OMITTED — the component NEVER
 * fabricates progress copy. Empty state is honest and forward-looking.
 *
 * Renders in three modes:
 *   - "first_time" → the "Start of your Cricket Journey" positioning card.
 *   - "early"      → identity + focus + honest future states.
 *   - "active" / "lapsed" → full "Where am I / What Changed / What's Next".
 */
import React, { useEffect, useState } from "react";
import { fetchCricketGraph } from "../lib/cricketGraph";

function _metricLabel(key) {
  if (!key) return "";
  return key.replace(/_/g, " ");
}

function _greeting(name) {
  const h = new Date().getHours();
  const prefix = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  return name ? `${prefix}, ${name}` : prefix;
}

export default function CricketGraphCard({ onOpenChat }) {
  const [g, setG] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchCricketGraph();
      if (cancelled) return;
      setG(data);
      setState("ready");
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === "loading") return null;
  if (!g) return null;

  // Never-populated first-time user: the "Start of your Cricket Journey" card.
  if (g.state === "first_time" || !g.has_content) {
    return (
      <div
        data-testid="cricket-journey-start"
        className="rounded-3xl border border-emerald-900/60 bg-neutral-900/70 p-6 my-6"
      >
        <div className="text-[11px] uppercase tracking-widest text-emerald-500 mb-3">
          Your Cricket Journey
        </div>
        <div
          data-testid="cricket-journey-start-title"
          className="text-2xl text-neutral-100 font-medium mb-3 leading-snug"
        >
          This is the start of your cricket journey.
        </div>
        <p
          data-testid="cricket-journey-start-body"
          className="text-[15px] text-neutral-300 leading-relaxed mb-5"
        >
          Every match. Every practice. Every video. Every breakthrough.
          Gully will remember what matters and show you where your
          cricket is going.
        </p>
        <button
          data-testid="cricket-journey-start-cta"
          onClick={onOpenChat}
          className="w-full py-3 rounded-full bg-emerald-500 text-neutral-950 font-medium hover:bg-emerald-400 transition-colors"
        >
          Start my cricket
        </button>
      </div>
    );
  }

  // Anything past first_time: render the "Your Cricket" snapshot.
  const identity = g.identity || {};
  const goal = g.goal;
  const focus = g.current_focus;
  const last = g.last_activity;
  const next = g.next_step;
  const streak = g.cricket_weeks?.consecutive || 0;
  const qualifiesThisWeek = !!g.cricket_weeks?.qualifies_this_week;
  const latestBaseline = (g.baselines || [])[0]?.latest;
  const pb = (g.progress_events || []).find((p) => p.kind === "personal_best");
  // XX.242a — "YOUR CRICKET HAS STARTED" heading fires the first time
  // Gully learns enough (early state, goal + focus present, no
  // activity yet). Once real activity lands the heading softens to
  // "Your Cricket".
  const justStarted = g.state === "early" && !!goal && !!focus
    && !latestBaseline && !pb;

  return (
    <div
      data-testid="cricket-graph-card"
      className="rounded-3xl border border-neutral-800 bg-neutral-900/70 p-6 my-6"
    >
      <div
        data-testid="cricket-graph-eyebrow"
        className={
          "text-[11px] uppercase tracking-widest mb-4 " +
          (justStarted ? "text-emerald-500" : "text-neutral-500")
        }
      >
        {justStarted ? "Your Cricket Has Started 🏏" : "Your Cricket"}
      </div>
      <div
        data-testid="cricket-graph-greeting"
        className="text-lg text-neutral-100 font-medium mb-5"
      >
        {_greeting(identity.name)}
      </div>

      {/* Where am I */}
      <div className="space-y-3 mb-5">
        {goal && (
          <div data-testid="cricket-graph-goal">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Goal
            </div>
            <div className="text-[15px] text-neutral-100">{goal}</div>
          </div>
        )}
        {focus && (
          <div data-testid="cricket-graph-focus">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Current Focus
            </div>
            <div className="text-[15px] text-neutral-100">{focus}</div>
          </div>
        )}
        {latestBaseline && (
          <div data-testid="cricket-graph-last-session">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
              Last Session
            </div>
            <div className="text-[15px] text-neutral-100">
              {latestBaseline.value}/{latestBaseline.total} on{" "}
              {_metricLabel((g.baselines || [])[0]?.metric_key)}
            </div>
          </div>
        )}
      </div>

      {/* What Changed */}
      {pb && (
        <div
          data-testid="cricket-graph-personal-best"
          className="rounded-2xl bg-emerald-900/20 border border-emerald-900/40 p-4 mb-5"
        >
          <div className="text-[11px] uppercase tracking-wider text-emerald-500 mb-1">
            Personal Best 🏏
          </div>
          <div className="text-[15px] text-emerald-200">
            {_metricLabel(pb.category)} · {pb.previous_value} → {pb.new_value}
          </div>
          {/* Iter XX.243 — natural share moment. Opt-in only. */}
          <a
            data-testid="cricket-graph-share-pb"
            href={
              "/pulse?prefill=" +
              encodeURIComponent(
                `Personal best · ${_metricLabel(pb.category)} · ${pb.previous_value} → ${pb.new_value}`
              )
            }
            className="inline-block mt-3 text-[12px] text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
          >
            Share to Gully →
          </a>
        </div>
      )}

      {/* Cricket Weeks — honest, no fake numbers. */}
      {(streak > 0 || qualifiesThisWeek) && (
        <div
          data-testid="cricket-graph-weeks"
          className="rounded-2xl bg-neutral-950/60 border border-neutral-800 p-4 mb-5"
        >
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
            Cricket Weeks
          </div>
          <div className="text-[15px] text-neutral-100">
            {streak > 0
              ? `${streak} consecutive week${streak > 1 ? "s" : ""} of real cricket`
              : "This week's cricket recorded"}
          </div>
        </div>
      )}

      {/* What's Next — deterministic, never fabricated. */}
      {next && (
        <div data-testid="cricket-graph-next-step" className="mb-5">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
            Next Step
          </div>
          <div className="text-[15px] text-neutral-100 mb-1">{next.text}</div>
          {next.why && (
            <div className="text-[13px] text-neutral-500">{next.why}</div>
          )}
        </div>
      )}

      <button
        data-testid="cricket-graph-continue-cta"
        onClick={onOpenChat}
        className="w-full py-3 rounded-full bg-emerald-500 text-neutral-950 font-medium hover:bg-emerald-400 transition-colors"
      >
        Continue my cricket
      </button>
    </div>
  );
}
